import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SetModalActionType } from './interfaces'
import { MODAL_STATE, MODAL_TYPES } from './enums'
import { RootState } from '@Redux/store'

interface ModalQueueState {
  current: SetModalActionType | null
  queue: SetModalActionType[]
  isOpen: any
  modalType: MODAL_TYPES
  lastActiveTab: string | null
}

const initialState: ModalQueueState = {
  current: null,
  queue: [],
  isOpen: MODAL_STATE.CLOSE,
  modalType: MODAL_TYPES.NONE as MODAL_TYPES,
  lastActiveTab: null,
}

const ModalSlice = createSlice({
  name: 'modals',
  initialState,
  reducers: {
    setModal(
      state,
      action: PayloadAction<
        Omit<SetModalActionType, 'isOpen'> & {
          returnToTab?: string
          keepPrevious?: boolean
        }
      >,
    ) {
      const newModal: SetModalActionType = {
        ...action.payload,
        isOpen: MODAL_STATE.OPEN,
      } as SetModalActionType

      if (action.payload.returnToTab) {
        state.lastActiveTab = action.payload.returnToTab
      }

      if (state.current && action.payload.keepPrevious) {
        state.queue.unshift({
          ...state.current,
          isOpen: MODAL_STATE.CLOSE,
        })
      } else if (state.current) {
        state.current = null
      }

      state.current = newModal
      state.modalType = newModal.modalType
      state.isOpen = newModal.isOpen
    },

    closeModal(state) {
      if (state.queue.length > 0) {
        const nextModal = state.queue.shift()!
        state.current = nextModal
        state.modalType = nextModal.modalType
        state.isOpen = nextModal.isOpen
      } else {
        state.current = null
        state.modalType = MODAL_TYPES.NONE
        state.isOpen = MODAL_STATE.CLOSE
        state.queue = []
      }
    },

    clearLastActiveTab(state) {
      state.lastActiveTab = null
    },

    closeAllModals(state) {
      state.current = null
      state.queue = []
      state.modalType = MODAL_TYPES.NONE
      state.isOpen = MODAL_STATE.CLOSE
    },

    clearModalQueue(state) {
      state.queue = []
      state.current = null
      state.modalType = MODAL_TYPES.NONE
      state.isOpen = MODAL_STATE.CLOSE
    },

    openModalFromSocket(
      state,
      action: PayloadAction<
        | {
            type: 'reality_check'
            data: {
              flag_reality_check_period: boolean
              period_start_session_time: number
              reality_check_period: number
              reality_check_last_time: string
            }
          }
        | {
            type: 'freeze_period'
            data: {
              flag_reality_check_period: boolean
              period_just_seconds_real_last_time: number
              reality_check_period: number
              reality_check_last_time: string
            }
          }
        | { type: 'inactivity_notice'; data: { seconds_inactivity_notice: number; flag_inactivity_notice: boolean } }
        | { type: 'inactivity_time' }
        | { type: 'new_terms'; data: { need_terms_and_conditions_accept: string } }
        | { type: 'exclude_kyc' }
        | { type: 'session_timeout'; data: { error_user_data: string } }
        | { type: 'user_not_active' }
        | { type: 'session_not_equal' }
      >,
    ) {
      let modal: SetModalActionType

      switch (action.payload.type) {
        case 'reality_check':
          modal = {
            modalType: MODAL_TYPES.REALITY_CHECK_PERIOD_MODAL,
            data: {
              flag_reality_check_period: action.payload.data.flag_reality_check_period,
              period_start_session_time: action.payload.data.period_start_session_time,
              reality_check_period: action.payload.data.reality_check_period,
              reality_check_last_time: action.payload.data.reality_check_last_time,
              isFreezePeriod: false,
            },
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'freeze_period':
          modal = {
            modalType: MODAL_TYPES.FREEZE_PERIOD_MODAL,
            data: {
              flag_reality_check_period: action.payload.data.flag_reality_check_period,
              period_just_seconds_real_last_time: action.payload.data.period_just_seconds_real_last_time,
              reality_check_period: action.payload.data.reality_check_period,
              reality_check_last_time: action.payload.data.reality_check_last_time,
              isFreezePeriod: true,
            },
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'inactivity_notice':
          modal = {
            modalType: MODAL_TYPES.INACTIVITY_NOTICE_MODAL,
            data: {
              seconds_inactivity_notice: action.payload.data.seconds_inactivity_notice,
              flag_inactivity_notice: action.payload.data.flag_inactivity_notice,
            },
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'inactivity_time':
          modal = {
            modalType: MODAL_TYPES.SESSION_TIMEOUT_MODAL,
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'new_terms':
          modal = {
            modalType: MODAL_TYPES.UPDATE_TERMS_AND_CONDITIONS_MODAL,
            data: {
              need_terms_and_conditions_accept: action.payload.data.need_terms_and_conditions_accept,
            },
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'exclude_kyc':
          modal = {
            modalType: MODAL_TYPES.EXCLUDE_KYC_MODAL,
            data: {},
            isOpen: MODAL_STATE.OPEN,
          }
          break

        case 'session_timeout':
          modal = {
            modalType: MODAL_TYPES.SESSION_TIMEOUT_MODAL,
            data: {
              error_user_data: action.payload.data.error_user_data,
            },
            isOpen: MODAL_STATE.OPEN,
          }
          break
        case 'user_not_active':
          modal = {
            modalType: MODAL_TYPES.CLOSE_ACCOUNT_MODAL,
            data: {},
            isOpen: MODAL_STATE.OPEN,
          }
          break
        case 'session_not_equal':
          modal = {
            modalType: MODAL_TYPES.CLOSE_ACCOUNT_MULTIPLE_MODAL,
            data: {},
            isOpen: MODAL_STATE.OPEN,
          }
          break

        default:
          return
      }

      const exists =
        state.queue.some((m) => m.modalType === modal.modalType) || state.current?.modalType === modal.modalType

      if (exists) return

      if (state.current) {
        state.queue.unshift({
          ...state.current,
          isOpen: MODAL_STATE.CLOSE,
        })
      }

      state.current = modal
      state.modalType = modal.modalType
      state.isOpen = modal.isOpen
    },
  },
})

export const isOpen = (state: RootState, modalType: MODAL_TYPES): boolean =>
  state.modals.isOpen && modalType === state.modals.modalType

export const isOpenCurrent = (state: RootState, modalType: MODAL_TYPES): boolean =>
  !!state.modals?.current?.isOpen && modalType === state.modals.current.modalType

export const getLastActiveTab = (state: RootState): string | null => state.modals.lastActiveTab

export const { setModal, closeModal, clearLastActiveTab, closeAllModals, clearModalQueue, openModalFromSocket } =
  ModalSlice.actions

export default ModalSlice.reducer
